
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { projectFormSchema, type ProjectFormValues } from "./form/types";
import { ContactSelector } from "./form/ContactSelector";
import { BasicInfoFields } from "./form/BasicInfoFields";
import { DateFields } from "./form/DateFields";
import { PricingFields } from "./form/PricingFields";
import { GitIntegrationFields } from "./form/GitIntegrationFields";
import { ComplexityField } from "./form/ComplexityField";
import { ProjectLeadField } from "./form/ProjectLeadField";
import { TeamMembersField } from "./form/TeamMembersField";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";

interface ProjectFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  initialData?: ProjectFormValues;
}

export const ProjectForm = ({ onSuccess, onCancel, initialData }: ProjectFormProps) => {
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();
  const { session } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const extractContactIds = () => {
    if (initialData && initialData.project_contacts && initialData.project_contacts.length > 0) {
      return initialData.project_contacts.map(pc => 
        pc.contacts && pc.contacts.id ? pc.contacts.id : pc.contact_id
      ).filter(Boolean);
    }
    return initialData?.contact_ids || [];
  };
  
  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      status: initialData?.status || "Not Started",
      start_date: initialData?.start_date || "",
      end_date: initialData?.end_date || "",
      contact_ids: extractContactIds(),
      pricing_type: initialData?.pricing_type || "flat",
      price: initialData?.price || 0,
      payment_date: initialData?.payment_date || "",
      recurring_start_date: initialData?.recurring_start_date || "",
      recurring_end_date: initialData?.recurring_end_date || "",
      repository_url: initialData?.repository_url || "",
      github_owner: initialData?.github_owner || "",
      repository_name: initialData?.repository_name || "",
      default_branch: initialData?.default_branch || "main",
      complexity: initialData?.complexity || "medium",
      project_lead_id: initialData?.project_lead_id || "",
      team_member_ids: initialData?.project_team_members?.map((tm: any) => tm.user_id) || [],
    },
  });

  useEffect(() => {
    console.log("Form values:", form.getValues());
    console.log("Initial data:", initialData);
    console.log("Extracted contact IDs:", extractContactIds());
  }, [initialData]);

  const handleSubmit = async (values: ProjectFormValues, createAnother: boolean = false) => {
    if (!currentOrganization?.id) {
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      const processFieldValue = (value: string | undefined | null) => {
        if (value === "none" || value === "") {
          return null;
        }
        return value;
      };
      
      const cleanedValues = {
        name: values.name.trim(),
        description: values.description,
        status: values.status,
        start_date: values.start_date || null,
        end_date: values.end_date || null,
        pricing_type: values.pricing_type,
        price: values.price,
        payment_date: values.payment_date || null,
        recurring_start_date: values.recurring_start_date || null,
        recurring_end_date: values.recurring_end_date || null,
        repository_url: values.repository_url || null,
        github_owner: values.github_owner || null,
        repository_name: values.repository_name || null,
        default_branch: values.default_branch || null,
        complexity: values.complexity,
        project_lead_id: values.project_lead_id || null,
      };

      if (initialData?.id) {
        const { data, error } = await supabase.functions.invoke("project-operations", {
          body: {
            action: "update",
            project: cleanedValues,
            projectId: initialData.id,
            contactIds: values.contact_ids,
            teamMemberIds: values.team_member_ids,
            organizationId: currentOrganization.id
          }
        });

        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Project updated successfully",
        });
        
        onSuccess?.();
      } else {
        const { data, error } = await supabase.functions.invoke("project-operations", {
          body: {
            action: "create",
            project: cleanedValues,
            userId: session?.user?.id,
            organizationId: currentOrganization.id,
            contactIds: values.contact_ids,
            teamMemberIds: values.team_member_ids
          }
        });

        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Project created successfully",
        });

        if (createAnother) {
          form.reset();
        } else {
          onSuccess?.();
        }
      }
    } catch (error) {
      console.error("Error saving project:", error);
      toast({
        title: "Error",
        description: "Failed to save project",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentOrganization) {
    return <div>Please select an organization to manage projects.</div>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((values) => handleSubmit(values, false))} className="space-y-4">
        <BasicInfoFields form={form} />
        <ContactSelector form={form} />
        <DateFields form={form} />
        <PricingFields form={form} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ComplexityField form={form} />
          <ProjectLeadField form={form} />
        </div>
        <TeamMembersField form={form} />
        <GitIntegrationFields form={form} />
        <div className="flex justify-between items-center">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : initialData ? 'Save Changes' : 'Create Project'}
            </Button>
            {!initialData && (
              <Button 
                type="button" 
                disabled={isSubmitting}
                onClick={() => form.handleSubmit((values) => handleSubmit(values, true))()}
              >
                {isSubmitting ? "Saving..." : "Save & Create Another"}
              </Button>
            )}
          </div>
        </div>
      </form>
    </Form>
  );
};
